import { useContext, useRef, useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router';
import { Box, Typography, Avatar, Chip, Tooltip, IconButton, Alert, Stack, Button, CircularProgress } from '@mui/joy';
import { motion, AnimatePresence } from 'framer-motion';

import CalendarMonthRoundedIcon    from '@mui/icons-material/CalendarMonthRounded';
import EditNoteRoundedIcon          from '@mui/icons-material/EditNoteRounded';
import DashboardRoundedIcon         from '@mui/icons-material/DashboardRounded';
import AssignmentRoundedIcon        from '@mui/icons-material/AssignmentRounded';
import PeopleRoundedIcon            from '@mui/icons-material/PeopleRounded';
import LocationOnRoundedIcon        from '@mui/icons-material/LocationOnRounded';
import MessageRoundedIcon           from '@mui/icons-material/MessageRounded';
import RuleRoundedIcon              from '@mui/icons-material/RuleRounded';
import LogoutRoundedIcon            from '@mui/icons-material/LogoutRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import ManageAccountsRoundedIcon    from '@mui/icons-material/ManageAccountsRounded';
import NotificationsOffRoundedIcon    from '@mui/icons-material/NotificationsOffRounded';
import NotificationsActiveRoundedIcon from '@mui/icons-material/NotificationsActiveRounded';
import PhoneIphoneRoundedIcon         from '@mui/icons-material/PhoneIphoneRounded';
import RefreshRoundedIcon            from '@mui/icons-material/RefreshRounded';
import SwapHorizRoundedIcon          from '@mui/icons-material/SwapHorizRounded';
import LockOpenRoundedIcon           from '@mui/icons-material/LockOpenRounded';
import ReceiptLongRoundedIcon        from '@mui/icons-material/ReceiptLongRounded';
import BarChartRoundedIcon           from '@mui/icons-material/BarChartRounded';
import EmojiEventsRoundedIcon        from '@mui/icons-material/EmojiEventsRounded';
import GavelRoundedIcon              from '@mui/icons-material/GavelRounded';
import DescriptionRoundedIcon        from '@mui/icons-material/DescriptionRounded';
import AccountBalanceWalletRoundedIcon from '@mui/icons-material/AccountBalanceWalletRounded';
import SettingsRoundedIcon           from '@mui/icons-material/SettingsRounded';
import GamepadRoundedIcon            from '@mui/icons-material/GamepadRounded';
import StorefrontRoundedIcon         from '@mui/icons-material/StorefrontRounded';
import HistoryRoundedIcon          from '@mui/icons-material/HistoryRounded';
import PaidRoundedIcon             from '@mui/icons-material/PaidRounded';
import CameraAltRoundedIcon        from '@mui/icons-material/CameraAltRounded';
import DeleteOutlineRoundedIcon    from '@mui/icons-material/DeleteOutlineRounded';
import Inventory2RoundedIcon       from '@mui/icons-material/Inventory2Rounded';
import PendingActionsRoundedIcon   from '@mui/icons-material/PendingActionsRounded';
import DeleteSweepRoundedIcon from '@mui/icons-material/DeleteSweepRounded';

import { AppContext } from '../context';
import {
    APIUploadAvatar, APIDeleteAvatar,
} from '../api';


// ─── Меню по ролям ────────────────────────────────────────────────────────────
const PHOTOGRAPHER_MENU = [
    {
        icon:  <CalendarMonthRoundedIcon sx={{ fontSize: 16 }} />,
        label: 'Мой график',
        desc:  'Расписание и назначения',
        path:  '/',
    },
    {
        icon:  <EditNoteRoundedIcon sx={{ fontSize: 16 }} />,
        label: 'Запрос на изменение',
        desc:  'Изменить график после дедлайна',
        path:  '/change-request',
    },
    // {
    //     dividerBefore: true,
    //     icon:  <AccountBalanceWalletRoundedIcon sx={{ fontSize: 16 }} />,
    //     label: 'Моя зарплата',
    //     desc:  'Расчёт за период',
    //     path:  '/my-salary',
    // },
    // {
    //     icon:  <GavelRoundedIcon sx={{ fontSize: 16 }} />,
    //     label: 'Мои штрафы',
    //     desc:  'Штрафы и списания',
    //     path:  '/my-fines',
    // },
];

const MANAGER_MENU = [
    {
        icon:  <DashboardRoundedIcon sx={{ fontSize: 16 }} />,
        label: 'Дашборд',
        desc:  'Общая картина недели',
        path:  '/manager',
        // без perms — виден всем авторизованным
    },
    {
        icon:  <AssignmentRoundedIcon sx={{ fontSize: 16 }} />,
        label: 'Назначить смены',
        desc:  'Распределить по точкам',
        path:  '/manager/assign',
        perms: ['schedule.view'],
    },
    {
        icon:  <RuleRoundedIcon sx={{ fontSize: 16 }} />,
        label: 'Запросы на изменение',
        desc:  'Одобрить или отклонить',
        path:  '/manager/change-requests',
        perms: ['schedule.change_requests.review'],
    },
    {
        icon:  <LockOpenRoundedIcon sx={{ fontSize: 16 }} />,
        label: 'Заявки на доступ',
        desc:  'Запросы на заполнение графика',
        path:  '/manager/fill-requests',
        perms: ['schedule.fill_requests.review'],
    },
    {
        icon:  <SwapHorizRoundedIcon sx={{ fontSize: 16 }} />,
        label: 'Замены',
        desc:  'Заявки на подмену',
        path:  '/manager/substitutions',
        perms: ['schedule.substitutions.review', 'schedule.substitutions.view'],
    },
    {
        icon:  <Inventory2RoundedIcon sx={{ fontSize: 16 }} />,
        label: 'Заявки на списание',
        desc:  'Одобрить списание материалов',
        path:  '/manager/write-offs',
        perms: ['finance.stock.writeoff.review'],
    },
    {
        icon:  <DeleteSweepRoundedIcon sx={{ fontSize: 16 }} />,
        label: 'Заявки на отмену чеков',
        desc:  'Одобрить отмену чеков от админов',
        path:  '/manager/order-deletion-requests',
        perms: ['finance.orders.delete_requests.review'],
    },
    // {
    //     dividerBefore: true,
    //     icon:  <ReceiptLongRoundedIcon sx={{ fontSize: 16 }} />,
    //     label: 'Чеки',
    //     desc:  'Продажи фотографов',
    //     path:  '/manager/orders',
    //     perms: ['finance.orders.view', 'finance.orders.create'],
    // },
    {
        dividerBefore: true,
        icon:  <PendingActionsRoundedIcon sx={{ fontSize: 16 }} />,
        label: 'Висящие чеки',
        desc:  'Неоформленные продажи по точкам',
        path:  '/manager/pending-sales',
        perms: ['finance.sale_notifications.review'],
    },
    {
        icon:  <EmojiEventsRoundedIcon sx={{ fontSize: 16 }} />,
        label: 'Премии',
        desc:  'Начисления сотрудникам',
        path:  '/manager/rewards',
        perms: ['finance.rewards.view', 'finance.rewards.create'],
    },
    {
        icon:  <GavelRoundedIcon sx={{ fontSize: 16 }} />,
        label: 'Штрафы',
        desc:  'Штрафы и списания',
        path:  '/manager/fines',
        perms: ['finance.fines.view', 'finance.fines.create'],
    },
    {
        icon:  <BarChartRoundedIcon sx={{ fontSize: 16 }} />,
        label: 'Статистика',
        desc:  'Отчёт за период',
        path:  '/manager/stats',
        perms: ['reports.stats', 'reports.view'],
    },
    {
        icon:  <DescriptionRoundedIcon sx={{ fontSize: 16 }} />,
        label: 'Excel-отчёт',
        desc:  'Выгрузка данных',
        path:  '/manager/report',
        perms: ['reports.excel'],
    },
    {
        icon:  <GamepadRoundedIcon sx={{ fontSize: 16 }} />,
        label: 'Роли',
        desc:  'Роли и доступы',
        path:  '/manager/roles',
        perms: ['roles.view'],
    },
    {
        icon: <PhoneIphoneRoundedIcon sx={{ fontSize: 16 }} />,
        label: 'Номера для оплаты',
        desc: 'Пул номеров для приёма переводов',
        path: '/manager/payment-numbers',
        perms: ['payment_numbers.view'],
    },
    {
        dividerBefore: true,
        icon:  <PeopleRoundedIcon sx={{ fontSize: 16 }} />,
        label: 'Сотрудники',
        desc:  'Список и управление',
        path:  '/manager/employees',
        perms: ['employees.view', 'employees.create', 'employees.update', 'employees.fire', 'employees.restore', 'employees.delete', 'employees.reset_password'],
    },
    {
        icon:  <StorefrontRoundedIcon sx={{ fontSize: 16 }} />,
        label: 'Рабочие аккаунты',
        desc:  'Аккаунты компьютеров на точках',
        path:  '/manager/workstations',
        perms: ['employees.manage_admins'],
    },
    {
        icon:  <HistoryRoundedIcon sx={{ fontSize: 16 }} />,
        label: 'История событий',
        desc:  'Все действия сотрудников',
        path:  '/manager/history',
        perms: ['events.view'],
    },
    {
        icon:  <Inventory2RoundedIcon sx={{ fontSize: 16 }} />,
        label: 'Товары и скидки',
        desc:  'Ассортимент услуг и скидок',
        path:  '/products',
        perms: ['finance.products.view'],
    },
    {
        icon:  <LocationOnRoundedIcon sx={{ fontSize: 16 }} />,
        label: 'Точки',
        desc:  'Рабочие локации',
        path:  '/manager/locations',
        perms: ['locations.create', 'locations.update', 'locations.delete', 'locations.change_default'],
    },
    {
        icon:  <MessageRoundedIcon sx={{ fontSize: 16 }} />,
        label: 'Сообщения',
        desc:  'Рассылка сообщений',
        path:  '/manager/messages',
        perms: ['system.messages.view', 'system.messages.create'],
    },
    {
        icon:  <AccountBalanceWalletRoundedIcon sx={{ fontSize: 16 }} />,
        label: 'Условия расчёта',
        desc:  '% фотографа, премии, оклады',
        path:  '/manager/salary',
        perms: ['finance.configs.view'],
    },
    {
        icon:  <PaidRoundedIcon sx={{ fontSize: 16 }} />,
        label: 'Отчёт по ЗП',
        desc:  'Расчёт зарплат по периодам',
        path:  '/manager/salary-report',
        perms: ['reports.view'],
    },
    {
        icon:  <SettingsRoundedIcon sx={{ fontSize: 16 }} />,
        label: 'Настройки',
        desc:  'Звук уведомлений, очистка месяца',
        path:  '/manager/settings',
    },
];

// ─── Меню рабочей точки ──────────────────────────────────────────
const WORKSTATION_MENU = [
    {
        icon:  <DashboardRoundedIcon sx={{ fontSize: 16 }} />,
        label: 'Главная',
        desc:  'Смена и продажи',
        path:  '/workstation',
    },
    {
        icon:  <ReceiptLongRoundedIcon sx={{ fontSize: 16 }} />,
        label: 'Чеки и продажи',
        desc:  'Оформление и история',
        path:  '/workstation/orders',
    },
    {
        icon:  <GavelRoundedIcon sx={{ fontSize: 16 }} />,
        label: 'Штрафы',
        desc:  'Штрафы и списания',
        path:  '/workstation/fines',
    },
    {
        icon:  <BarChartRoundedIcon sx={{ fontSize: 16 }} />,
        label: 'Статистика',
        desc:  'Отчёт по точке',
        path:  '/workstation/stats',
    },
];

const getInitials = (account) => {
    if (!account) return '?';
    const f = account.first_name?.[0] || '';
    const l = account.last_name?.[0]  || '';
    return (f + l).toUpperCase() || account.email?.[0]?.toUpperCase() || '?';
};

const ROLE_LABELS = {
    manager:            'Руководитель',
    photographer:       'Фотограф',
    admin:              'Админ точки',
    admin_photographer: 'Админ + Фотограф',
    workstation:        'Рабочий аккаунт',
};

const ROLE_CHIP_STYLES = {
    manager:            { color: 'var(--joy-palette-primary-300)', bg: 'rgba(162,38,226,0.12)', border: 'rgba(162,38,226,0.25)' },
    photographer:       { color: 'var(--joy-palette-primary-300)', bg: 'rgba(162,38,226,0.08)', border: 'rgba(162,38,226,0.18)' },
    admin:              { color: '#3b82f6',                        bg: 'rgba(59,130,246,0.10)', border: 'rgba(59,130,246,0.25)' },
    admin_photographer: { color: '#f59e0b',                        bg: 'rgba(245,158,11,0.10)',  border: 'rgba(245,158,11,0.25)' },
    workstation:        { color: '#22c55e',                        bg: 'rgba(34,197,94,0.12)',  border: 'rgba(34,197,94,0.30)' },
};

// ─── Push кнопка ──────────────────────────────────────────────────────────────
function PushButton() {
    const { pushPermission, pushStatus, requestPush, pwaStatus } = useContext(AppContext);

    const isGranted    = pushPermission === 'granted';
    const isDenied     = pushPermission === 'denied';
    const isRequesting = pushStatus === 'requesting';
    const isIOS        = pwaStatus.platform === 'ios';
    const needsInstall = pwaStatus.needsInstall;

    let icon  = <NotificationsOffRoundedIcon sx={{ fontSize: 17 }} />;
    let color = 'neutral';
    let tip   = 'Включить Push-уведомления';

    if (isGranted) {
        icon  = <NotificationsActiveRoundedIcon sx={{ fontSize: 17 }} />;
        color = 'success';
        tip   = 'Push-уведомления активны';
    } else if (isDenied) {
        icon  = <NotificationsOffRoundedIcon sx={{ fontSize: 17 }} />;
        color = 'danger';
        tip   = 'Push заблокированы — нажмите для инструкции';
    } else if (needsInstall && isIOS) {
        icon  = <PhoneIphoneRoundedIcon sx={{ fontSize: 17 }} />;
        color = 'warning';
        tip   = 'Установите как PWA для уведомлений';
    }  

    if (isGranted) return null;
    
    return (
        <Tooltip title={tip} variant="soft" size="sm">
            <IconButton
                size="md"
                variant="soft"
                color={color}
                onClick={requestPush}
                loading={isRequesting}
                disabled={isGranted}
                sx={{
                    borderRadius: 'var(--joy-radius-md)',
                    height: "fit-content",
                    ...(isGranted && { opacity: 1, pointerEvents: 'none' }),
                    ...(isDenied  && {
                        animation: 'pulse 2s infinite',
                        '@keyframes pulse': {
                            '0%,100%': { opacity: 1 },
                            '50%':     { opacity: 0.55 },
                        },
                    }),
                }}
            >
                {icon}
            </IconButton>
        </Tooltip>
    );
}

// ─── Кнопка принудительного обновления ────────────────────────────────────────
function RefreshButton() {
    const [spinning, setSpinning] = useState(false);

    const handleRefresh = () => {
        if (spinning) return;
        setSpinning(true);
        setTimeout(() => window.location.reload(), 150);
    };

    return (
        <Tooltip title="Обновить данные" variant="soft" size="sm">
            <IconButton
                size="md"
                variant="soft"
                color="neutral"
                onClick={handleRefresh}
                sx={{
                    borderRadius: 'var(--joy-radius-md)',
                    height: 'fit-content',
                }}
            >
                <motion.span
                    animate={{ rotate: spinning ? 360 : 0 }}
                    transition={{ duration: 0.6, ease: 'linear' }}
                    style={{ display: 'flex' }}
                >
                    <RefreshRoundedIcon sx={{ fontSize: 17 }} />
                </motion.span>
            </IconButton>
        </Tooltip>
    );
}

// ─── iOS Help Modal ───────────────────────────────────────────────────────────
function IOSHelpModal() {
    const { pushModalIOSHelp, setPushModalIOSHelp, pwaStatus } = useContext(AppContext);

    return (
        <AnimatePresence>
            {pushModalIOSHelp && (
                <>
                    <motion.div
                        key="overlay"
                        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                        onClick={() => setPushModalIOSHelp(false)}
                        style={{
                            position: 'fixed', inset: 0, zIndex: 9000,
                            background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(6px)',
                        }}
                    />
                    <motion.div
                        key="card"
                        initial={{ opacity: 0, y: 40, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0,  scale: 1 }}
                        exit={{   opacity: 0, y: 40,  scale: 0.95 }}
                        transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
                        style={{
                            position: 'fixed', bottom: 24, left: 16, right: 16, zIndex: 9001,
                            borderRadius: 20,
                            background: 'var(--joy-palette-background-level1)',
                            border: '1px solid rgba(162,38,226,0.25)',
                            padding: 24,
                            boxShadow: '0 24px 64px rgba(0,0,0,0.7)',
                        }}
                    >
                        <Box sx={{ fontSize: 36, textAlign: 'center', mb: 1.5 }}>
                            {pwaStatus.isStandalone ? '🔔' : '📲'}
                        </Box>
                        <Typography sx={{ fontWeight: 800, fontSize: 17, color: 'var(--joy-palette-text-primary)', textAlign: 'center', mb: 0.75 }}>
                            {pwaStatus.isStandalone ? 'Включите уведомления' : 'Установите приложение'}
                        </Typography>
                        <Typography sx={{ fontSize: 13, color: 'var(--joy-palette-text-tertiary)', textAlign: 'center', mb: 2.5 }}>
                            {pwaStatus.isStandalone
                                ? 'Разрешите уведомления в настройках браузера для этого сайта.'
                                : 'На iPhone уведомления работают только через установленное PWA.'}
                        </Typography>

                        {!pwaStatus.isStandalone && (
                            <Box sx={{
                                bgcolor: 'rgba(162,38,226,0.08)', borderRadius: 'var(--joy-radius-lg)',
                                p: 2, mb: 2.5,
                            }}>
                                <Stack spacing={1.2}>
                                    {[
                                        '1. Откройте Safari и перейдите на этот сайт',
                                        '2. Нажмите кнопку «Поделиться» (квадрат со стрелкой ↑)',
                                        '3. Выберите «На экран "Домой"»',
                                        '4. Нажмите «Добавить» и откройте с экрана',
                                    ].map((s, i) => (
                                        <Typography key={i} sx={{ fontSize: 13, color: 'var(--joy-palette-text-secondary)' }}>
                                            {s}
                                        </Typography>
                                    ))}
                                </Stack>
                            </Box>
                        )}

                        <Button
                            fullWidth
                            onClick={() => setPushModalIOSHelp(false)}
                            sx={{
                                borderRadius: 'var(--joy-radius-md)', fontWeight: 700,
                                background: 'linear-gradient(135deg, var(--joy-palette-primary-500) 0%, var(--joy-palette-primary-300) 100%)',
                                color: '#fff', border: 'none',
                            }}
                        >
                            Понятно
                        </Button>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
}

// ════════════════════════════════════════════════════════════════════
export default function Header() {
    const { account, token, setAccount, logout, showToast } = useContext(AppContext);
    const navigate            = useNavigate();
    const location            = useLocation();
    const [open, setOpen]     = useState(false);
    const menuRef             = useRef(null);
    const fileInputRef        = useRef(null);
    const [avatarBusy, setAvatarBusy] = useState(false);

    const isWorkstation   = account?.role === 'workstation';
    const isPureManager   = account?.role === 'manager';
    const canFillSchedule = account?.role === 'photographer' || account?.role === 'admin' || account?.role === 'admin_photographer';
    const userPerms       = account?.permissions || [];
    const canEditAvatar   = userPerms.includes('employees.update');

    // Менеджерские пункты, до которых у пользователя есть доступ по правам
    const managerItems = MANAGER_MENU.filter(item => {
        // Пункты без perms (Дашборд, Сотрудники, Точки) — только для тех, кто
        // в "менеджерской зоне" (чистый manager/admin); фотографам сюда не лазить
        if (!item.perms || item.perms.length === 0) {
            return isPureManager;
        }
        return item.perms.some(p => userPerms.includes(p));
    });

    let menu;
    if (isWorkstation) {
        menu = WORKSTATION_MENU;
    } else if (canFillSchedule) {
        // Фотограф / Админ+Фотограф: ВСЕГДА видит свои "фотографские" пункты,
        // плюс менеджерские, к которым выдали права.
        // Первый менеджерский пункт получает dividerBefore, чтобы визуально отделить.
        const decoratedManager = managerItems.map((it, idx) =>
            idx === 0 ? { ...it, dividerBefore: true } : it
        );
        menu = [...PHOTOGRAPHER_MENU, ...decoratedManager];
    } else {
        // Чистый manager / admin / кто угодно без права заполнять график
        menu = managerItems;
    }

    useEffect(() => {
        const handler = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    useEffect(() => { setOpen(false); }, [location.pathname]);

    const handleNav    = (path) => { setOpen(false); navigate(path); };
    const handleLogout = async () => { setOpen(false); await logout(); };

    // ── Загрузка аватарки ────────────────────────────────────
    const handleAvatarPick = () => fileInputRef.current?.click();

    const handleAvatarChange = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (file.size > 5 * 1024 * 1024) {
            showToast('Файл больше 5 МБ', 'danger');
            e.target.value = '';
            return;
        }
        if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
            showToast('Только JPG / PNG / WEBP', 'danger');
            e.target.value = '';
            return;
        }

        setAvatarBusy(true);
        const res = await APIUploadAvatar(token, account.id, file);
        setAvatarBusy(false);
        e.target.value = '';

        if (res?.error) {
            const msg = typeof res.error === 'string' ? res.error : (res.error.error || 'Не удалось загрузить');
            showToast(msg, 'danger');
            return;
        }
        // Cache-bust: добавляем timestamp, чтобы браузер не показал старую картинку
        setAccount({
            ...res,
            avatar_url: res.avatar_url ? `${res.avatar_url}?t=${Date.now()}` : null,
        });
        showToast('Аватарка обновлена ✓');
    };

    const handleAvatarDelete = async () => {
        setAvatarBusy(true);
        const res = await APIDeleteAvatar(token, account.id);
        setAvatarBusy(false);

        if (res?.error) {
            showToast('Не удалось удалить', 'danger');
            return;
        }
        setAccount({ ...account, avatar_url: null });
        showToast('Аватарка удалена');
    };

    return (
        <Box
            component="header"
            sx={{
                position:       'sticky',
                top:            0,
                zIndex:         100,
                display:        'flex',
                alignItems:     'center',
                justifyContent: 'space-between',
                px:             { xs: 2, sm: 3 },
                py:             1.5,
                backdropFilter: 'none',
                WebkitBackdropFilter: 'none',
                borderBottom:   '1px solid var(--joy-palette-divider)',
            }}
        >
            <Stack direction={'row'} sx={{ maxWidth: (isPureManager || managerItems.length > 0) ? 900 : 680, mx: 'auto', width: '100%', justifyContent: "space-between" }}>
                {/* Логотип */}
                <Box
                    onClick={() => navigate(isWorkstation ? '/workstation' : isPureManager ? '/manager' : '/')}
                    sx={{ display: 'flex', alignItems: 'center', gap: 1.2, cursor: 'pointer', userSelect: 'none' }}
                >
                    <Box
                        component="img"
                        src="/logo.png"
                        alt="Фото G"
                        sx={{
                            width: 32,
                            objectFit:   'cover',
                        }}
                    />
                    <Typography sx={{
                        fontSize: 17, fontWeight: 800,
                        color: 'primary.400'
                    }}>
                        Фото G
                    </Typography>
                    {(() => {
                        const role = account?.role || 'photographer';
                        // Роль — внутренняя метка для руководителя; самим сотрудникам её не показываем
                        if (['photographer', 'admin', 'admin_photographer'].includes(role)) return null;
                        const style = ROLE_CHIP_STYLES[role] || ROLE_CHIP_STYLES.photographer;
                        return (
                            <Chip
                                size="sm"
                                startDecorator={<ManageAccountsRoundedIcon sx={{ fontSize: 11 }} />}
                                sx={{
                                    bgcolor: style.bg, color: style.color,
                                    fontWeight: 700, fontSize: 10, letterSpacing: '0.04em',
                                    border: `1px solid ${style.border}`,
                                    display: { xs: 'none', sm: 'flex' },
                                }}
                            >
                                {ROLE_LABELS[role] || role}
                            </Chip>
                        );
                    })()}
                </Box>
                
                <Stack direction={"row"} spacing={0.5} sx={{ alignItems: "center" }}>
                    <RefreshButton />
                    <PushButton />

                    {/* Аватар + дропдаун */}
                    <Box ref={menuRef} sx={{ position: 'relative' }}>
                        <Box
                            onClick={() => setOpen(v => !v)}
                            sx={{
                                display: 'flex', alignItems: 'center', gap: 1,
                                cursor: 'pointer', px: 1.2, py: 0.6,
                                borderRadius: 'var(--joy-radius-lg)',
                                border: '1px solid transparent', transition: 'all .18s', userSelect: 'none',
                                '&:hover': { bgcolor: 'var(--joy-palette-background-level2)', borderColor: 'var(--joy-palette-divider)' },
                            }}
                        >
                            <Avatar
                                size="sm"
                                src={account?.avatar_url || undefined}
                                sx={{
                                    width: 32, height: 32, fontSize: 12, fontWeight: 700,
                                    bgcolor: (ROLE_CHIP_STYLES[account?.role] || ROLE_CHIP_STYLES.photographer).bg,
                                    color: (ROLE_CHIP_STYLES[account?.role] || ROLE_CHIP_STYLES.photographer).color,
                                    border: `1.5px solid ${(ROLE_CHIP_STYLES[account?.role] || ROLE_CHIP_STYLES.photographer).border}`,
                                }}
                            >
                                {getInitials(account)}
                            </Avatar>

                            <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                                <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'var(--joy-palette-text-primary)', lineHeight: 1.2 }}>
                                    {account?.first_name || '—'}
                                </Typography>
                                <Typography sx={{ fontSize: 10, color: 'var(--joy-palette-text-tertiary)', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                                    {ROLE_LABELS[account?.role] || 'Фотограф'}
                                </Typography>
                            </Box>

                            <motion.div animate={{ rotate: open ? 180 : 0 }} transition={{ duration: 0.2 }}>
                                <KeyboardArrowDownRoundedIcon sx={{ fontSize: 18, color: 'var(--joy-palette-text-secondary)' }} />
                            </motion.div>
                        </Box>

                        <AnimatePresence>
                            {open && (
                                <motion.div
                                    initial={{ opacity: 0, y: -8, scale: 0.96 }}
                                    animate={{ opacity: 1, y: 0,  scale: 1    }}
                                    exit={{   opacity: 0, y: -8, scale: 0.96  }}
                                    transition={{ duration: 0.16, ease: [0.22, 1, 0.36, 1] }}
                                    style={{
                                        position: 'absolute', top: 'calc(100% + 8px)', right: 0,
                                        zIndex: 200, minWidth: 230, transformOrigin: 'top right',
                                    }}
                                >
                                    <Box sx={{
                                        bgcolor: 'var(--joy-palette-background-level1)',
                                        border: '1px solid var(--joy-palette-divider)',
                                        borderRadius: 'var(--joy-radius-lg)',
                                        boxShadow: '0 16px 48px rgba(0,0,0,0.55)',
                                        overflowY: 'scroll', py: 0.5, maxHeight: '70vh'
                                    }}>
                                        {/* Профиль с аватаркой */}
                                        <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                            {/* Аватарка с двумя кнопками поверх */}
                                            <Box sx={{
                                                position: 'relative',
                                                '@media (hover: hover)': {
                                                    '& .avatar-overlay': { opacity: 0 },
                                                    '&:hover .avatar-overlay': { opacity: 1 },
                                                },
                                            }}>
                                                <Avatar
                                                    src={account?.avatar_url || undefined}
                                                    sx={{
                                                        width: 56, height: 56,
                                                        fontSize: 18, fontWeight: 700,
                                                        bgcolor: (ROLE_CHIP_STYLES[account?.role] || ROLE_CHIP_STYLES.photographer).bg,
                                                        color:   (ROLE_CHIP_STYLES[account?.role] || ROLE_CHIP_STYLES.photographer).color,
                                                        border:  `1.5px solid ${(ROLE_CHIP_STYLES[account?.role] || ROLE_CHIP_STYLES.photographer).border}`,
                                                    }}
                                                >
                                                    {getInitials(account)}
                                                </Avatar>

                                                {/* Кнопки управления аватаркой — на десктопе оверлеем при ховере, на мобильном — полоской снизу */}
                                                {!avatarBusy && canEditAvatar && (
                                                    <Box
                                                        className="avatar-overlay"
                                                        sx={{
                                                            position: 'absolute',
                                                            // На десктопе — закрывает всё, на мобильном — только нижнюю треть
                                                            inset: { xs: 'auto 0 0 0', sm: 0 },
                                                            height: { xs: '40%', sm: 'auto' },
                                                            // Только нижние углы скруглены на мобильном
                                                            borderRadius: { xs: '0 0 100px 100px', sm: '50%' },
                                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                            gap: 0.5,
                                                            bgcolor: 'rgba(0,0,0,0.7)',
                                                            opacity: { xs: 1, sm: 0 },
                                                            transition: 'opacity .15s',
                                                            cursor: 'pointer',
                                                            pb: { xs: 0.2, sm: 0 },
                                                        }}
                                                    >
                                                        <Box
                                                            onClick={(ev) => { ev.stopPropagation(); handleAvatarPick(); }}
                                                            title="Сменить"
                                                            sx={{
                                                                width: 18, height: 18, borderRadius: '50%',
                                                                bgcolor: 'rgba(255,255,255,0.95)',
                                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                                '&:hover': { bgcolor: '#fff' },
                                                            }}
                                                        >
                                                            <CameraAltRoundedIcon sx={{ fontSize: 11, color: '#111' }} />
                                                        </Box>
                                                        {account?.avatar_url && (
                                                            <Box
                                                                onClick={(ev) => { ev.stopPropagation(); handleAvatarDelete(); }}
                                                                title="Удалить"
                                                                sx={{
                                                                    width: 18, height: 18, borderRadius: '50%',
                                                                    bgcolor: 'rgba(248,113,113,0.95)',
                                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                                    '&:hover': { bgcolor: '#f87171' },
                                                                }}
                                                            >
                                                                <DeleteOutlineRoundedIcon sx={{ fontSize: 11, color: '#fff' }} />
                                                            </Box>
                                                        )}
                                                    </Box>
                                                )}

                                                {/* Спиннер при загрузке */}
                                                {avatarBusy && (
                                                    <Box sx={{
                                                        position: 'absolute', inset: 0,
                                                        borderRadius: '50%',
                                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                        bgcolor: 'rgba(0,0,0,0.65)',
                                                    }}>
                                                        <CircularProgress size="sm" sx={{ '--CircularProgress-size': '20px' }} />
                                                    </Box>
                                                )}
                                            </Box>

                                            {/* Имя и почта */}
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Typography sx={{
                                                    fontSize: 13, fontWeight: 700,
                                                    color: 'var(--joy-palette-text-primary)',
                                                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                                }}>
                                                    {account?.full_name || account?.first_name || '—'}
                                                </Typography>
                                                <Typography sx={{
                                                    fontSize: 11, color: 'var(--joy-palette-text-tertiary)', mt: 0.2,
                                                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                                }}>
                                                    {account?.email}
                                                </Typography>
                                            </Box>
                                        </Box>

                                        {/* Скрытый file input */}
                                        <input
                                            ref={fileInputRef}
                                            type="file"
                                            accept="image/jpeg,image/png,image/webp"
                                            onChange={handleAvatarChange}
                                            style={{ display: 'none' }}
                                        />

                                        <Box sx={{ borderTop: '1px solid var(--joy-palette-divider)' }} />

                                        {/* Пункты */}
                                        {menu.map((item) => (
                                            <Box key={item.path}>
                                                {item.dividerBefore && (
                                                    <Box sx={{ borderTop: '1px solid var(--joy-palette-divider)', my: 0.5 }} />
                                                )}
                                                <Box
                                                    onClick={() => handleNav(item.path)}
                                                    sx={{
                                                        display: 'flex', alignItems: 'center', gap: 1.2,
                                                        px: 2, py: 0.9, cursor: 'pointer', transition: 'background .15s',
                                                        bgcolor: location.pathname === item.path
                                                            ? 'var(--joy-palette-background-level2)'
                                                            : 'transparent',
                                                        '&:hover': { bgcolor: 'var(--joy-palette-background-level2)' },
                                                    }}
                                                >
                                                    <Box sx={{
                                                        color: location.pathname === item.path
                                                            ? 'var(--joy-palette-primary-400)'
                                                            : 'var(--joy-palette-text-secondary)',
                                                        display: 'flex', flexShrink: 0,
                                                    }}>
                                                        {item.icon}
                                                    </Box>
                                                    <Box>
                                                        <Typography sx={{
                                                            fontSize: 13,
                                                            fontWeight: location.pathname === item.path ? 700 : 500,
                                                            color: location.pathname === item.path
                                                                ? 'var(--joy-palette-primary-300)'
                                                                : 'var(--joy-palette-text-primary)',
                                                            lineHeight: 1.3,
                                                        }}>
                                                            {item.label}
                                                        </Typography>
                                                        <Typography sx={{ fontSize: 10, color: 'var(--joy-palette-text-tertiary)' }}>
                                                            {item.desc}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </Box>
                                        ))}

                                        <Box sx={{ borderTop: '1px solid var(--joy-palette-divider)', my: 0.5 }} />

                                        {/* Выход */}
                                        <Box
                                            onClick={handleLogout}
                                            sx={{
                                                display: 'flex', alignItems: 'center', gap: 1.2,
                                                px: 2, py: 0.9, cursor: 'pointer', transition: 'background .15s',
                                                '&:hover': { bgcolor: 'rgba(248,113,113,0.08)' },
                                            }}
                                        >
                                            <LogoutRoundedIcon sx={{ fontSize: 16, color: 'var(--joy-palette-danger-400)' }} />
                                            <Typography sx={{ fontSize: 13, fontWeight: 500, color: 'var(--joy-palette-danger-400)' }}>
                                                Выйти
                                            </Typography>
                                        </Box>
                                    </Box>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </Box>
                </Stack>
            </Stack>

            <IOSHelpModal />
        </Box>
    );
}